import React, { useState, useRef, useMemo, useEffect } from "react";
import {
  Users,
  CheckCircle,
  Clock,
  Upload,
  Download,
  Save,
  AlertCircle,
} from "lucide-react";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { GenericTable } from "@shared/components/GenericTable";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Input } from "@shared/ui/input";
import { toast } from "@shared/ui/use-toast";
import { TableColumn } from "@shared/types/table";
import { PageHeader } from "@shared/components/PageHeader";
import { useParams } from "react-router-dom";
import {
  useDownloadMeterCSV,
  useBulkRequestMeterList,
  useUploadMeterCSV,
  useSaveMeterReading,
} from "@features/cx/disconnect/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { PAGE_SIZE } from "@shared/utils/constants";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { normalizeDate } from "@shared/utils/dateUtils";

interface MeterReadingData {
  id: string;
  accountNo: string;
  consumerName: string;
  utilityService: string;
  meterNo: string;
  deviceNo: string;
  previousReading: number;
  currentReading: string | number;
  readingDate: string;
  status: "Collected" | "Pending";
  jobCard: string;
  isEditing?: boolean;
}

const MeterReadingsBulkDisconnect = () => {
  const { id } = useParams<{ id: string }>();
  const remoteUtilityId = useRemoteUtilityId();

  const [page, setPage] = useState(1);
  const [limit] = useState(PAGE_SIZE);

  const { data: meterListResponse, isLoading: isMeterListLoading } =
    useBulkRequestMeterList({
      batchId: id || "",
      remoteUtilityId: Number(remoteUtilityId),
      page: page,
      limit: limit,
    });

  const { mutateAsync: downloadMeterCSV, isPending: isDownloading } =
    useDownloadMeterCSV();

  const { mutateAsync: uploadMeterCSV, isPending: isUploadingCSV } =
    useUploadMeterCSV();
  const { mutateAsync: saveMeterReading } = useSaveMeterReading();

  const [savingId, setSavingId] = useState<string | null>(null);

  const data = meterListResponse || {};
  console.log("data", data);
  const totalCount =
    meterListResponse?.count ||
    meterListResponse?.total ||
    meterListResponse?.results?.length ||
    0;

  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localData, setLocalData] = useState<any[]>([]);
  const [uploadErrorReport, setUploadErrorReport] = useState<any>(null);

  useEffect(() => {
    const results = meterListResponse?.results;
    console.log("results", results);
    if (results) {
      const uniqueDataMap = new Map();
      results.forEach((item: any) => {
        const uniqueId = item.meterDetails?.id || item.remoteMeterId;
        if (uniqueId && !uniqueDataMap.has(uniqueId)) {
          uniqueDataMap.set(uniqueId, {
            ...item,
            id: uniqueId,
            currentReading: item.meterDetails?.currentReading ?? "",
            readingDate:
              normalizeDate(item.meterDetails?.currentReadingDate) ?? "",
          });
        }
      });
      setLocalData(Array.from(uniqueDataMap.values()));
    }
  }, [meterListResponse]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const pagination = useMemo(
    () => ({
      currentPage: page,
      totalPages,
      totalItems: totalCount,
      itemsPerPage: PAGE_SIZE,
      startItem: (page - 1) * PAGE_SIZE + 1,
      endItem: Math.min(page * PAGE_SIZE, totalCount),
    }),
    [page, totalPages, totalCount],
  );

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleInputChange = (
    id: string,
    value: string,
    field: string = "currentReading",
  ) => {
    setLocalData((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    );
  };

  const handleSave = async (id: string) => {
    const item = localData.find((d: any) => d.id === id);
    if (!item?.currentReading) {
      toast({
        title: "Warning",
        description: "Please enter a reading value before saving.",
        variant: "destructive",
      });
      return;
    }

    const payload = {
      remote_utility_id: Number(remoteUtilityId),
      consumer_no: item.consumerNo,
      consumer_name: `${item.firstName || ""} ${item.lastName || ""}`.trim(),
      meter_no: item.meterDetails?.meterNumber,
      read: item.currentReading,
      read_date: normalizeDate(item.readingDate),
      read_status:
        item.meterDetails?.currentReadingMeterStatusDisplay.toUpperCase(),
    };

    setSavingId(id);
    try {
      await saveMeterReading(payload);
    } catch (error) {
      // Error handled by hook
    } finally {
      setSavingId(null);
    }
  };

  const handleDownload = async () => {
    if (!id) return;
    try {
      await downloadMeterCSV(id);
    } catch (error) {
      // Error handled by hook or global interceptor
    }
  };

  const handleUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file && id) {
      try {
        const response = await uploadMeterCSV({ batchId: id, file });
        if (response?.error_count > 0) {
          setUploadErrorReport(response);
        }
        if (fileInputRef.current) fileInputRef.current.value = "";
      } catch (error) {
        // Error handled by hook
      }
    }
  };

  const columns: TableColumn<MeterReadingData>[] = [
    {
      key: "consumerNo",
      header: "Account No",
    },
    {
      key: "firstName",
      header: "Consumer Name",
      render: (val: any, row: any) => {
        const fullName = `${val || ""} ${row.lastName || ""}`.trim();
        return <span>{fullName || "N/A"}</span>;
      },
    },
    {
      key: "utilityService",
      header: "Utility Service",
      render: (val: any, row: any) => {
        const utilityService = `${row.meterDetails?.productCode || ""}`.trim();
        return <span>{utilityService || "N/A"}</span>;
      },
    },
    {
      key: "deviceNo",
      header: "Device No",
      render: (val: any, row: any) => {
        const deviceNo = `${row.meterDetails?.deviceNo || ""}`.trim();
        return <span>{deviceNo || "N/A"}</span>;
      },
    },
    {
      key: "previousReading",
      header: "Previous Reading",
      render: (val: any, row: any) => {
        const previousReading =
          `${row.meterDetails?.previousReading || ""}`.trim();
        return <span>{previousReading || "N/A"}</span>;
      },
    },
    {
      key: "currentReading",
      header: "Current Reading",
      render: (val: any, row: any) => {
        const readingValue = val ?? "";
        return row.status === "Collected" ? (
          <div className="bg-green-50 border border-green-200 rounded-md px-3 py-1 text-center font-medium text-green-700 w-24">
            {readingValue}
          </div>
        ) : (
          <Input
            className="w-24 h-9 bg-white text-center"
            placeholder="Enter"
            value={readingValue}
            onChange={(e) => handleInputChange(row.id, e.target.value)}
          />
        );
      },
    },
    {
      key: "readingDate",
      header: "Reading Date",
      render: (val: any, row: any) => {
        const rawDate = val ?? "";
        // Convert MM-DD-YYYY to YYYY-MM-DD for date input if needed
        let formattedDate = rawDate;
        if (rawDate.includes("-") && rawDate.split("-")[2]?.length === 4) {
          const [m, d, y] = rawDate.split("-");
          formattedDate = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
        }

        return row.status === "Collected" ? (
          <div className="bg-green-50 border border-green-200 rounded-md px-3 py-1 text-center font-medium text-green-700 w-32">
            {rawDate}
          </div>
        ) : (
          <Input
            type="date"
            className="w-40 h-9 bg-white text-center"
            value={formattedDate}
            onChange={(e) =>
              handleInputChange(row.id, e.target.value, "readingDate")
            }
          />
        );
      },
    },
    {
      key: "readingStatus",
      header: "Reading Status",
      render: (val: any, row: any) => {
        const readingStatus =
          row.meterDetails?.currentReadingMeterStatusDisplay;
        return (
          <Badge className={getStatusBadgeClass(readingStatus)}>
            {readingStatus}
          </Badge>
        );
      },
    },
    {
      key: "meterStatus",
      header: "Status",
      render: (val: any, row: any) => {
        const statusValue = row.meterDetails?.status || "N/A";
        return (
          <Badge className={getStatusBadgeClass(statusValue)}>
            {statusValue}
          </Badge>
        );
      },
    },
    {
      key: "id",
      header: "Action",
      render: (_, row) => (
        <Button
          size="sm"
          variant={row.status === "Collected" ? "outline" : "default"}
          className={
            row.status === "Collected"
              ? "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
              : "bg-blue-600 hover:bg-blue-700"
          }
          onClick={() => handleSave(row.id)}
          disabled={!!savingId}
        >
          {row.status === "Collected"
            ? "Saved"
            : savingId === row.id
              ? "Saving..."
              : "Save"}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header section */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-orange-50 rounded-lg">
            <Clock className="w-5 h-5 text-orange-600" />
          </div>
          <PageHeader title="Upload Meter Readings" />
        </div>
      </div>

      <div className="flex items-center gap-4">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".csv"
          className="hidden"
        />
        <Button
          variant="outline"
          onClick={handleUpload}
          disabled={isUploadingCSV}
        >
          <Upload className="w-4 h-4 mr-2" />
          {isUploadingCSV ? "Uploading..." : "Upload Readings CSV"}
        </Button>
        <Button
          variant="outline"
          onClick={handleDownload}
          disabled={isDownloading}
        >
          <Download className="w-4 h-4 mr-2" />
          {isDownloading ? "Downloading..." : "Download Readings"}
        </Button>
        <span className="text-sm text-neutral-400">
          Or enter readings manually below
        </span>
      </div>

      {/* KPI Section */}
      <div className="grid grid-cols-3 gap-4">
        <QuickStatsCard
          title="Total Consumers"
          count={
            data?.summary?.total_consumers ?? data?.summary?.totalConsumers ?? 0
          }
          icon={
            <div className="p-2 bg-blue-50 rounded-full">
              <Users className="w-5 h-5 text-blue-500" />
            </div>
          }
        />
        <QuickStatsCard
          title="Readings Collected"
          count={
            data?.summary?.total_reading_collected ??
            data?.summary?.totalReadingCollected ??
            0
          }
          icon={
            <div className="p-2 bg-green-50 rounded-full">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
          }
          className="bg-green-50/50"
        />
        <QuickStatsCard
          title="Pending Readings"
          count={
            data?.summary?.total_reading_pending ??
            data?.summary?.totalReadingPending ??
            0
          }
          icon={
            <div className="p-2 bg-orange-50 rounded-full">
              <Clock className="w-5 h-5 text-orange-500" />
            </div>
          }
          className="bg-orange-50/50"
        />
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-xl shadow-sm border border-neutral-100 overflow-hidden">
        <GenericTable
          data={localData}
          columns={columns}
          loading={isMeterListLoading || loading}
          rowKey="id"
          emptyMessage="No meter readings found"
          pagination={pagination}
          onPageChange={handlePageChange}
        />
      </div>
      {/* Error Report Dialog */}
      <Dialog
        open={!!uploadErrorReport}
        onOpenChange={() => setUploadErrorReport(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Upload Error Report
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <p className="text-neutral-500 font-medium">
                Found{" "}
                <span className="font-bold text-red-600">
                  {uploadErrorReport?.error_count}
                </span>{" "}
                errors in the uploaded CSV.
              </p>
              <div className="px-3 py-1 bg-green-50 text-green-700 rounded-full font-medium">
                Successfully processed: {uploadErrorReport?.success_count}
              </div>
            </div>

            <div className="border rounded-xl overflow-hidden shadow-sm">
              <div className="max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-neutral-50 border-b sticky top-0">
                    <tr>
                      <th className="px-4 py-3 font-semibold text-neutral-600">
                        Row
                      </th>
                      <th className="px-4 py-3 font-semibold text-neutral-600">
                        Meter No
                      </th>
                      <th className="px-4 py-3 font-semibold text-neutral-600">
                        Error Message
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {uploadErrorReport?.errors?.map((err: any, idx: number) => (
                      <tr key={idx} className="hover:bg-neutral-50/50">
                        <td className="px-4 py-3 text-neutral-500 font-medium">
                          {err.row}
                        </td>
                        <td className="px-4 py-3 font-bold text-neutral-800">
                          {err.meter_no}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-block px-2 py-0.5 bg-red-50 text-red-600 rounded text-xs font-medium border border-red-100">
                            {err.error}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeterReadingsBulkDisconnect;
